import type { FC, ReactNode } from 'react'
import type { FilterOption } from '@/types'

import { cn } from '@/lib/utils'

interface FilterBarProps {
    label: string
    options: Array<FilterOption>
    value: string
    onChange: (value: string) => void
    allLabel?: string
    total: number
    className?: string
}

/**
 * Toggle-button filter row. Options with no entries stay visible (the
 * taxonomy is part of the story) but cannot be selected.
 */
const FilterBar: FC<FilterBarProps> = ({ label, options, value, onChange, allLabel = 'All', total, className }): ReactNode => {
    const all: FilterOption = { value: 'all', label: allLabel, count: total }

    return (
        <div role='group' aria-label={label} className={cn('flex flex-wrap gap-x-1 gap-y-2', className)}>
            {[all, ...options].map((option) => {
                const active = option.value === value
                const disabled = option.count === 0 && !active

                return (
                    <button
                        key={option.value}
                        type='button'
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'group/filter inline-flex min-h-10 items-center gap-2 rounded-sm border px-3 text-[0.875rem] transition-colors duration-200',
                            active
                                ? 'border-foreground bg-foreground text-background'
                                : 'border-transparent text-foreground hover:border-border-strong',
                            disabled && 'cursor-not-allowed text-muted-foreground opacity-60 hover:border-transparent'
                        )}
                    >
                        <span>{option.label}</span>

                        <span
                            className={cn(
                                'font-mono text-[0.6875rem] tabular-nums',
                                active ? 'text-background/70' : 'text-muted-foreground'
                            )}
                        >
                            {String(option.count).padStart(2, '0')}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}

export default FilterBar
